import axios from 'axios';
import store from '@/store';
import { v4 as uuidv4 } from 'uuid';
import { router } from '@/main';

const baseURL = process.env.VUE_APP_API_URL;

const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;
    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const apiRequest = async (method, url, data = null, params = {}, skipAccountCheck = false) => {
    if (!skipAccountCheck && store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const config = {
                method: method,
                url: `${baseURL}/${url}`,
                headers: getAuthHeader(token),
                params: { account: store.state.user.user.account_id, ...params },
                data: data,
            };
            return await axios(config);
        });
    } catch (error) {
        throw error;
    }
};

const userService = {
    fetchUsers(page = 0, size = 20) {
        return apiRequest('get', `users/account/${store.state.user.user.account_id}`, null, { page, size });
    },

    fetchUser(userId, userEmail = null) {
        return apiRequest('get', `users/${userId}`, null, { email: userEmail }, true);
    },

    /**
     * Whether the user who has just signed up has a pending invitation. It answers for the
     * email in their own Firebase token, before they have any account claims.
     * Returns uuid (only when that email is verified), email, organization_name and status.
     */
    checkInvitation() {
        return apiRequest('get', 'users/check-invitation', null, {}, true);
    },

    async inviteUser(userData) {
        const requestData = {
            user_id: uuidv4(),
            username: userData.username,
            account_id: store.state.user.user.account_id,
            permission: userData.permission,
            role: userData.role,
            capabilities: userData.capabilities,
            // Canales que atiende. Lista vacía = todos.
            assigned_channel_ids: userData.assigned_channel_ids || [],
            contact_phone: userData.contact_phone || '',
            notifications_config: {
                push_notification_ids: [],
                active_notifications: {
                    conversation: {
                        conversation_sentiment_changed: true,
                        conversation_window_created: true,
                        conversation_window_updated: true,
                    },
                },
            },
            email: userData.email,
        };

        const response = await apiRequest('post', `users/invite/${store.state.user.user.account_id}`, requestData);
        return {
            uuid: requestData.user_id,
            response,
        };
    },

    editUser(userId, updateData) {
        return apiRequest('post', `users/${userId}`, updateData);
    },

    addPushNotificationToken(userId, token) {
        const requestData = {
            token,
        };
        return apiRequest('post', `users/${userId}/add-notification-id`, requestData);
    },

    deleteUser(userId) {
        return apiRequest('delete', `users/${userId}`);
    },

    acceptInvitation(userId, externalId) {
        const requestData = {
            external_id: externalId,
        };
        return apiRequest('post', `users/${userId}/accept-invitation`, requestData, {}, true);
    },

    getStorageURL(userId, contentType) {
        const uuid = uuidv4();
        const fileName = `${uuid}`;
        const accountId = store.state.user.user.account_id;

        return apiRequest('get', `users/account/${accountId}/media/upload-url`, null, {
            userId,
            contentType,
            fileName,
        });
    },
};

export default userService;
